// Normalized analytics taxonomy — stable machine-readable identifiers so that
// GA4 / PostHog reporting doesn't fragment across slightly different display names.

export type PageType =
  | 'home'
  | 'company'
  | 'work_index'
  | 'case_study'
  | 'solutions'
  | 'solution'
  | 'vixi'
  | 'vixi_product'
  | 'contact'
  | 'other';

export type SolutionId =
  | 'mixed_reality'
  | 'augmented_reality'
  | 'creative_production'
  | 'ai_powered'
  | 'vixi';

export type VixiProductId =
  | 'capture'
  | 'magic'
  | 'streaming'
  | 'lightshow'
  | 'add_ons'
  | 'licensing';

export const PRODUCT_FAMILY_VIXI = 'vixi';

const VIXI_PRODUCT_BY_SLUG: Record<string, VixiProductId> = {
  capture: 'capture',
  magic: 'magic',
  streaming: 'streaming',
  lightshow: 'lightshow',
  'light-show': 'lightshow',
  'add-ons': 'add_ons',
  addons: 'add_ons',
  licensing: 'licensing',
};

/** Normalize a Vixi product from a slug, path or display name. */
export function normalizeVixiProduct(input?: string): VixiProductId | undefined {
  if (!input) return undefined;
  const raw = input.toLowerCase().trim();
  const slug = raw.split('/').filter(Boolean).pop() || raw;
  if (VIXI_PRODUCT_BY_SLUG[slug]) return VIXI_PRODUCT_BY_SLUG[slug];
  if (raw.includes('light')) return 'lightshow';
  if (raw.includes('add')) return 'add_ons';
  if (raw.includes('licens')) return 'licensing';
  if (raw.includes('stream')) return 'streaming';
  if (raw.includes('magic')) return 'magic';
  if (raw.includes('capture') || raw.includes('fancam')) return 'capture';
  return undefined;
}

/** Normalize a solution / experience name or slug to a stable identifier. */
export function normalizeSolution(input?: string): SolutionId | undefined {
  if (!input) return undefined;
  const raw = input.toLowerCase().trim();
  if (raw.includes('vixi')) return 'vixi';
  if (raw.includes('mixed') || raw === 'mr') return 'mixed_reality';
  if (raw.includes('augmented') || raw === 'ar') return 'augmented_reality';
  if (raw.includes('creative') || raw.includes('production')) return 'creative_production';
  if (raw.includes('ai')) return 'ai_powered';
  return undefined;
}

/** Normalize the current route to a coarse page type. */
export function getPageType(pathname: string = window.location.pathname): PageType {
  const path = pathname.replace(/\/+$/, '').toLowerCase() || '/';
  if (path === '/') return 'home';
  if (path === '/company') return 'company';
  if (path === '/work') return 'work_index';
  if (path === '/solutions') return 'solutions';
  if (path === '/contact') return 'contact';
  if (path === '/vixi') return 'vixi';
  if (path.startsWith('/vixi/')) return 'vixi_product';
  return 'other';
}

/** Resolve the Vixi product for the current route, when there is one. */
export function getVixiProductFromPath(pathname: string = window.location.pathname): VixiProductId | undefined {
  if (!pathname.toLowerCase().startsWith('/vixi/')) return undefined;
  return normalizeVixiProduct(pathname);
}
